import styled from '@emotion/styled'
import { PrismicRichText } from '@prismicio/react'
import { OVERLAY_TEXT_BOX_PADDING } from '../../../theme/theme'
import { ScrollOverMediaDocumentDataCoversItem } from '../../../../types.generated'
import { asText } from '@prismicio/client'
import {
  FadeInParagraphByParagraph,
  FadeInWholeBlock,
} from './effects/RichTextEffects'
import { VIEWPORT_HEIGHT } from '../../../util/viewportHeight'

export interface CoversInterface {
  covers: ScrollOverMediaDocumentDataCoversItem[]
}

export function Covers(props: CoversInterface) {
  const coversExceptRootCover = props.covers?.slice(1) ?? []

  return (
    <>
      {coversExceptRootCover.map((cover, index) => (
        <CoverRenderer
          key={index}
          cover={cover}
          ContainerComponent={CoverContainer}
        />
      ))}
    </>
  )
}

export function RootCover(props: CoversInterface) {
  const cover = props.covers?.[0]
  if (!cover) {
    return null
  }

  return <CoverRenderer cover={cover} ContainerComponent={RootCoverContainer} />
}

interface ContainerWithChildrenProps {
  coverContainerCss: string
  children: React.ReactNode
}

function CoverRenderer({
  cover,
  ContainerComponent,
}: {
  cover: ScrollOverMediaDocumentDataCoversItem
  ContainerComponent: React.ComponentType<ContainerWithChildrenProps>
}) {
  const coverContainerCss = asText(cover.cover_container_css)

  const renderContent = () => {
    switch (cover.effect_type) {
    case 'fade_in_paragraph_by_paragraph':
      return (
        <>
          <CoverContent>
            <FadeInParagraphByParagraph richText={cover.text} />
          </CoverContent>
          <CoverCaption>
            <FadeInParagraphByParagraph richText={cover.caption} />
          </CoverCaption>
        </>
      )

    case 'fade_in_whole_block':
      return (
        <>
          <CoverContent>
            <FadeInWholeBlock>
              <PrismicRichText field={cover.text} />
            </FadeInWholeBlock>
          </CoverContent>
          <CoverCaption>
            <FadeInWholeBlock>
              <PrismicRichText field={cover.caption} />
            </FadeInWholeBlock>
          </CoverCaption>
        </>
      )

    default:
      return (
        <>
          <CoverContent>
            <PrismicRichText field={cover.text} />
          </CoverContent>
          <CoverCaption>
            <PrismicRichText field={cover.caption} />
          </CoverCaption>
        </>
      )
    }
  }

  return (
    <ContainerComponent coverContainerCss={coverContainerCss}>
      {renderContent()}
    </ContainerComponent>
  )
}

const CoverContainer = styled.div<{ coverContainerCss: string }>`
  position: relative;
  width: 100%;
  min-height: ${VIEWPORT_HEIGHT};
  pointer-events: none;

  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;

  ${({ theme }) => theme.scrollOverMediaCoverContainerCss}
  ${({ coverContainerCss }) => coverContainerCss}
`

const RootCoverContainer = styled(CoverContainer)`
  position: absolute;
  top: 0px;
`

const BaseStyle = styled.div`
  display: flex;
  flex-direction: column;
  text-align: center;
  padding: ${OVERLAY_TEXT_BOX_PADDING};

  width: 100%;
  max-width: min(850px, 100%);
  color: white;

  overflow: hidden;

  background: black;
  * {
    color: white;
  }
`

const CoverContent = styled(BaseStyle)`
  * {
    pointer-events: auto;
  }
  ${({ theme }) => theme.scrollOverMediaCoverCss}
`

const CoverCaption = styled(CoverContent)`
  * {
    pointer-events: auto;
  }
  ${({ theme }) => theme.scrollOverMediaCoverCaptionCss}
`
